"use client"


import React, { useRef, useState } from 'react'

import useProjectStore from "@/store/projectStore";
import useUserStore from "@/store/userStore";
import useStore from "@/store/store";

import { getPlanBySlug } from "@/app/enum-subscription";

import { ThumbsUp, CircleOff } from "lucide-react"


const requirements = [
    {
        "type": "allowed",
        "label": "Clear human face",
        "image": "https://rs.magicface.co/indiesites/lipsync-show/app/allow-01.webp"
    },
    {
        "type": "allowed",
        "label": "With rotation angle",
        "image": "https://rs.magicface.co/indiesites/lipsync-show/app/allow-02.webp"
    },
    {
        "type": "allowed",
        "label": "3D Cartoon",
        "image": "https://rs.magicface.co/indiesites/lipsync-show/app/allow-03.webp"
    },
    {
        "type": "not_allowed",
        "label": ">1 face or small face",
        "image": "https://rs.magicface.co/indiesites/lipsync-show/app/notallow-01.webp"
    },
    {
        "type": "not_allowed",
        "label": "Animal face",
        "image": "https://rs.magicface.co/indiesites/lipsync-show/app/notallow-02.webp"
    },
    // {
    //     "type": "not_allowed",
    //     "label": "Cartoon or anime",
    //     "image": "https://rs.magicface.co/indiesites/lipsync-show/app/notallow-03.webp"
    // }
]


const Step1Video = ({ onHandleInputChange }) => {
    const uploadRef = useRef(null);
    const fileInput = useRef(null);

    const { userPlan } = useUserStore()
    const currentPlan = getPlanBySlug(userPlan);
    const { openPricingModal } = useStore()

    const { projVideo, setProjVideo } = useProjectStore()

    /**
     * 获取用户允许的最大视频上传大小（以字节为单位）
     * 层级递进检查：pro > premium > base/free
     * @returns {number|null} 返回最大字节数，如果是 null 表示无限制
     */
    const getMaxVideoSizeLimit = () => {
        if (currentPlan.features.unlimited_upload) {
            return null; // null 表示无限制
        }

        console.log("currentPlan.features.video_upload_limit", currentPlan.features.video_upload_limit);

        return currentPlan.features.video_upload_limit * 1024 * 1024;
    }


    const triggerFileInput = () => {
        fileInput.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        uploadRef.current.style.background = 'oklch(var(--b3))';
        event.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = () => {
        uploadRef.current.style.background = 'none';
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        handleFiles(event);
    };

    const handleFiles = (event) => {
        const files = event.target.files || event.dataTransfer.files;
        const videoFile = files[0];

        if (!videoFile) return;

        const videoElement = document.getElementById('slot_video');

        // 释放之前的 Blob URL，避免内存泄漏
        if (videoElement.src && videoElement.src.startsWith('blob:')) {
            URL.revokeObjectURL(videoElement.src);
        }

        // 重置视频元素状态
        videoElement.removeAttribute('src');
        videoElement.load();

        const videoURL = URL.createObjectURL(videoFile);

        // 使用 onloadedmetadata 而不是 addEventListener，避免监听器累积
        videoElement.onloadedmetadata = () => {
            const duration = parseFloat(videoElement.duration.toFixed(2));
            const width = videoElement.videoWidth;
            const height = videoElement.videoHeight;

            console.log("视频信息 - 宽度:", width, "高度:", height, "时长:", duration, "文件大小:", videoFile.size);

            if (duration > 600) {
                alert('Video uploaded duration not more than 10 minutes');
                resetFileInput();
                return;
            }

            if (duration < 3) {
                alert('Video uploaded duration should not less than 3 seconds');
                resetFileInput();
                return;
            }

            // 检查视频文件大小权限
            const maxVideoSize = getMaxVideoSizeLimit();
            if (maxVideoSize !== null && videoFile.size > maxVideoSize) {
                const maxSizeMB = (maxVideoSize / (1024 * 1024)).toFixed(0);
                openPricingModal({
                    title: "Video Too Large",
                    description: `Your current plan limits video uploads to ${maxSizeMB}MB. Upgrade your subscription to upload larger videos and enjoy higher quality creations.`
                });
                resetFileInput();
                return;
            }

            if (width < 512 || height < 512) {
                alert('Video width and height must be at least 512px');
                resetFileInput();
                return;
            }

            onHandleInputChange("video", videoFile);

            setProjVideo({
                type: 'upload',
                source: videoURL,
                name: videoFile.name,
                duration,
                width,
                height
            })
        };

        // 设置新的视频源
        videoElement.src = videoURL;
    };

    const resetFileInput = () => {
        fileInput.current.value = null;
        setProjVideo({
            type: 'upload',
            source: null,
            name: '',
            duration: 0,
            width: 0,
            height: 0
        });
    };

    return (
        <div className="card-property">
            <div
                className="file-upload-container bg-base-200 border border-base-content/40 border-dashed"
                ref={uploadRef}
                onClick={triggerFileInput}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleFileDrop}
            >
                <input
                    id="inputFileVideo"
                    type="file"
                    ref={fileInput}
                    onChange={handleFiles}
                    style={{ display: 'none' }}
                />


                <div className={projVideo.source ? "block" : "hidden"}>
                    <div className="flex justify-center">
                        <video id="slot_video" src={projVideo.source} className="mb-2 w-32 h-20" />
                    </div>
                    <div>{projVideo.name}</div>
                </div>

                <div className={projVideo.source ? "hidden" : "flex flex-col items-center"}>
                    <div className="mb-4">
                        <img src="https://rs.magicface.co/indiesites/lipsync-show/app/File-Video.webp" alt="video-example" className="w-32" />
                    </div>
                    <div className="text-white">
                        <span className="underline">Click</span>
                        <span> or drag original video here</span>
                    </div>
                    <div className="mt-2 text-xs text-warning/90">
                        mp4, mov, webm, avi file in 5 min, less than 50MB
                    </div>
                </div>

            </div>

            <div className="mt-8 mb-4 text-sm text-base-content/60">Lip Sync Requirements</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
                {requirements.map((requirement) => (
                    <div className={`rounded-lg p-2 mb-4 md:mb-0 relative
                        ${requirement.type === "allowed" ? "bg-lime-900" : "bg-red-900"}
                    `} key={requirement.label}>
                        <div className={`absolute top-[0] right-[0] p-2 rounded-xl shadow-xl
                            ${requirement.type === "allowed" ? "bg-lime-900 text-lime-300" : "bg-red-900 text-red-300"}`
                        }>
                            {requirement.type === "allowed" ? <ThumbsUp size={24} /> : <CircleOff size={24} />}
                        </div>
                        <img src={requirement.image} alt={requirement.label} className="w-full" />
                        <div className="text-sm text-base-content w-full md:w-auto mt-2">
                            {requirement.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Step1Video;
